import {
  fillUserMessageTemplate,
  toUserMessageLine,
  USER_MESSAGE_MAX_PARAMS,
  USER_MESSAGE_PARAM_VALUE_MAX_LENGTH,
  USER_MESSAGE_TEXT_MAX_LENGTH,
  type UserMessage,
} from "@atoms/atom-types";
import { translate } from "../i18n/TranslationBackendRegistry";

/**
 * What the lookup of a key returns when the catalog has no text for it. It
 * holds a NUL character, which no catalog text has, and neither a
 * placeholder nor a nesting marker, so a translation backend returns it as
 * it is.
 */
const MISSING_TRANSLATION = "\u0000atoms:missing-translation";

/**
 * The longest sentence shown: a default text of full length with every
 * parameter of full length filled in. A catalog text is the project's own
 * and has no limit of the wire, so the result is cut here.
 */
const RULE_SENTENCE_MAX_LENGTH =
  USER_MESSAGE_TEXT_MAX_LENGTH +
  USER_MESSAGE_MAX_PARAMS * USER_MESSAGE_PARAM_VALUE_MAX_LENGTH;

/**
 * The text of `key` in the project's catalog (active language, else the
 * fallback language of the backend), with its placeholders unfilled.
 *
 * The lookup passes NO parameters. Their names come from the server, and a
 * translation library reads names such as `context`, `lng` or
 * `defaultValue` as options. Only a string counts as a text: a library can
 * resolve a key such as `app.errors.toString` to a function. A backend that
 * throws has no text.
 */
function lookUpRuleTemplate(key: string | undefined): string | undefined {
  if (key === undefined) {
    return undefined;
  }
  try {
    const text: unknown = translate(key, MISSING_TRANSLATION);
    return typeof text === "string" && text !== MISSING_TRANSLATION
      ? text
      : undefined;
  } catch {
    // A translation backend of the project that throws for this key must
    // not take down the error that is being shown: no text.
    return undefined;
  }
}

/**
 * The template of a rule whose key has no text in the catalog: the default
 * text, which is written in the source language of the project. A session
 * in another language therefore reads the precise sentence untranslated
 * (decision O9 of atoms/atoms2#96, option a). Returning nothing here instead
 * shows the translated sentence of the class.
 */
function getTemplateWithoutTranslation(
  userMessage: UserMessage,
): string | undefined {
  return userMessage.defaultText;
}

/**
 * The sentence of a rule error of the project, ready to show: the catalog
 * text of its key, else its default text, with the parameters filled in by
 * the framework's own single pass (a value that looks like a placeholder
 * stays text) and made one line. Nothing when the message has no usable
 * text; the caller then shows the sentence of the class.
 *
 * The default text and the parameters never reach the translation library.
 * Plural forms and formats of the library do not apply to a rule sentence.
 */
export function renderRuleMessage(
  userMessage: UserMessage | undefined,
): string | undefined {
  if (!userMessage) {
    return undefined;
  }
  const template =
    lookUpRuleTemplate(userMessage.key) ??
    getTemplateWithoutTranslation(userMessage);
  if (template === undefined) {
    return undefined;
  }
  const sentence = toUserMessageLine(
    fillUserMessageTemplate(template, userMessage.params),
    RULE_SENTENCE_MAX_LENGTH,
  );
  return sentence.length > 0 ? sentence : undefined;
}
